
import { useEffect, useState } from "react";
import { useCheckoutSession } from "./CheckoutSession";
import { useSearchParams } from "react-router";
import { z } from "zod";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { useUser } from "~/hooks/use-user";
import type { CheckoutProfileResult } from "~/routes/api.events.$eventId.checkout-profile";
import { track } from "~/utils/analytics";
import { CheckoutPhoneAuthentication } from "./CheckoutPhoneAuthentication";
import {
  CheckoutQuestionStep,
  CheckoutTextField,
} from "./CheckoutQuestionStep";

export const checkoutNameSchema = z
  .string()
  .trim()
  .min(1, "Enter the name you'd like people to see.")
  .max(100, "Name cannot be longer than 100 characters.")
  .regex(/\p{L}/u, "Name must contain at least one letter.")
  .refine(
    (name) => name.toLowerCase() !== "anonymous user",
    "Enter the name you'd like people to see.",
  );

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
};

export function CheckoutDetailsStep({
  plan,
  authenticated,
  userExists,
}: Props) {
  const { user } = useUser();
  const [, setSearchParams] = useSearchParams();
  const initialName =
    user?.name && user.name.toLowerCase() !== "anonymous user" ? user.name : "";
  const checkout = useCheckoutSession();
  const [name, setName] = useState(checkout.draft.profile.name || initialName);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const hasVerifiedPhone =
    authenticated && userExists && !!user?.phoneNumber?.trim();

  const setStep = (step: "tickets" | "details" | "payment") => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("step", step);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  };

  const reloadAtStep = (step: "details" | "payment") => { void checkout.refreshUser(step); };
  const updateDraft = checkout.update;
  const profile = checkout.draft.profile;
  useEffect(() => { updateDraft({ profile: { ...profile, name } }); }, [name, profile, updateDraft]);

  const saveName = async () => {
    const validation = checkoutNameSchema.safeParse(name);
    if (!validation.success) {
      setFieldError(validation.error.issues[0]?.message);
      return;
    }

    setLoading(true);
    setError(null);
    setFieldError(undefined);
    if (!await checkout.flush() || !checkout.canContinue()) { setLoading(false); return; }
    if (name.trim() === initialName && checkoutNameSchema.safeParse(initialName).success) {
      setStep("payment");
      return;
    }
    const formData = new FormData();
    formData.append("intent", "save-name");
    formData.append("name", validation.data);

    try {
      const response = await fetch(`/api/events/${plan.id}/checkout-profile`, {
        method: "POST",
        body: formData,
      });
      const result = (await response.json()) as CheckoutProfileResult;
      if (!result.ok) {
        setFieldError(result.fieldErrors?.name);
        throw new Error(result.error ?? "Check your name and try again.");
      }

      track("checkout_details_submitted", {
        event_id: plan.id,
        event_title: plan.title,
      });
      reloadAtStep("payment");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Your name could not be saved. Please try again.",
      );
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0">
        <StepIndicator
          currentStep={2}
          showTicketStep
          stepActions={{ 1: () => setStep("tickets") }}
        />
      </div>

      {hasVerifiedPhone ? (
        <CheckoutQuestionStep
          title="What's your name?"
          description="Use your real name. This will appear on your ticket and in the plan."
          onSubmit={saveName}
          onBack={() => setStep("tickets")}
          loading={loading}
          error={error}

        >
          <CheckoutTextField
            label="Your name"
            autoComplete="name"
            placeholder="First and last name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setFieldError(undefined);
              setError(null);
            }}
            error={fieldError}
          />
        </CheckoutQuestionStep>
      ) : (
        <CheckoutPhoneAuthentication
          eventId={plan.id}
          onComplete={() => void checkout.refreshUser("payment", true)}
        />
      )}
    </div>
  );
}
